import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { addDays } from '../planMode';
import { planDayDate, planOffsetLabel } from '../rolloutDayCopy';
import { useRollout } from './RolloutContext';
import { daysBetween, railLabelsThatFit, railPosition, type PlanStepView, type StepTone } from './rolloutModel';
import { railPhaseLabels } from './railPhaseLabels';
import { rolloutMessages } from './rolloutMessages';
import type { MilestoneFacts } from './useMilestoneFacts';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const POINT_CLASS: Record<StepTone, string | undefined> = {
  ok: styles.ptOk,
  sch: styles.ptSch,
  bad: styles.ptBad,
  miss: styles.ptMiss,
  plain: styles.ptPlain,
};

/** "21 May" of a day. */
function shortDay(intl: ReturnType<typeof useIntl>, day: string): string {
  return intl.formatDate(new Date(`${day}T00:00:00Z`), { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

const pos = (left: number): React.CSSProperties => ({ left: `${left}%` });

/**
 * The whole rollout on one line (design rollout-plan a1, b1 `.rail`): a point per step, a diamond
 * for release day and the Canvas mark, the labels, and the Today line. Hidden on a phone (d1).
 */
const RolloutRailComponent: React.FC<{ steps: PlanStepView[]; facts: MilestoneFacts }> = ({ steps, facts }) => {
  const intl = useIntl();
  const { plan, today } = useRollout();
  const visible = steps.filter((step) => step.day.skipped !== true);
  const { releaseDate } = plan;
  if (releaseDate === null || visible.length === 0) {
    return null;
  }
  const offsets = [...visible.map((step) => step.day.offsetDays), 0, 1];
  const first = Math.min(...offsets);
  const last = Math.max(...offsets);
  const at = (offset: number) => railPosition(offset, first, last);
  const todayOffset = daysBetween(releaseDate, today);
  const showToday = todayOffset >= first && todayOffset <= last;
  // Each phase label ends before the next one starts: a long translation ("DIA DE LANÇAMENTO")
  // ran into "DEPOIS" (QA 2026-10-28, litr2-2). It is cut with "…" and keeps the full text as a tip.
  const phaseLabels = railPhaseLabels(
    [
      { key: 'build', text: intl.formatMessage(rolloutMessages.phaseBuild) },
      { key: 'release', text: intl.formatMessage(rolloutMessages.phaseRelease) },
      { key: 'after', text: intl.formatMessage(rolloutMessages.phaseRailAfter) },
    ],
    [0, Math.max(0, at(0) - 4), Math.min(90, at(1) + 8)]
  );
  // A label that would print on top of another one is left out; its point stays (QA finding F13).
  const labels = railLabelsThatFit(offsets, first, last);
  const firstDay = addDays(releaseDate, first);
  const lastDay = addDays(releaseDate, last);

  return (
    <div className={styles.railWrap}>
      <div
        className={styles.rail}
        role="img"
        aria-label={intl.formatMessage(rolloutMessages.railLabel, {
          first: planDayDate(intl, firstDay),
          last: planDayDate(intl, lastDay),
        })}
      >
        <div className={styles.axis}>{showToday && <i style={{ width: `${at(todayOffset)}%` }} />}</div>
        {phaseLabels.map((label) => (
          <span key={label.key} className={styles.railPhase} style={label.style} title={label.text}>
            {label.text}
          </span>
        ))}
        {visible.map((step) => (
          <span
            key={step.key}
            className={clsx(styles.pt, POINT_CLASS[step.tone])}
            style={pos(at(step.day.offsetDays))}
          />
        ))}
        <span className={styles.ms} style={pos(at(0))} />
        <span className={clsx(styles.ms, facts.canvasDone ? styles.msDone : styles.msMan)} style={pos(at(1))} />
        {showToday && (
          <span className={styles.now} style={pos(at(todayOffset))}>
            <span>{intl.formatMessage(rolloutMessages.today)}</span>
          </span>
        )}
        {labels.map((offset) => (
          <span key={offset} className={clsx(styles.lb, offset === 0 && styles.lbZero)} style={pos(at(offset))}>
            <b>{planOffsetLabel(intl, offset)}</b>
            <small>{shortDay(intl, addDays(releaseDate, offset))}</small>
          </span>
        ))}
      </div>
    </div>
  );
};

export const RolloutRail = TypedMemo(RolloutRailComponent);
